import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import ShopLayout from '../../components/shop/ShopLayout';
import SEOHead, { generateOrganizationSchema, generateWebsiteSchema } from '../../components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

function ShopHomepage() {
  const [banners, setBanners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [news, setNews] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loading, setLoading] = useState(true);
  const newsScrollRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner(prev => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchData = async () => {
    try {
      const [bannersRes, testimonialsRes, newsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/b2c/cms/banners/public?position=home_top`),
        axios.get(`${API}/api/b2c/cms/testimonials/public?limit=10`),
        axios.get(`${API}/api/b2c/cms/news/public?limit=6`),
        axios.get(`${API}/api/b2c/cms/site-settings`)
      ]);
      
      setBanners(bannersRes.data.banners || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
      setNews(newsRes.data.news || []);
      setSiteSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollNews = (direction) => {
    if (newsScrollRef.current) {
      const scrollAmount = 300;
      newsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Generate combined structured data for homepage
  const structuredData = siteSettings ? {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(siteSettings),
      generateWebsiteSchema(siteSettings)
    ]
  } : null;

  if (loading) {
    return (
      <ShopLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#2B3380] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      {/* SEO Meta Tags */}
      <SEOHead
        title={siteSettings?.meta_title || "ZOBOX - Buy Certified Refurbished Phones & Laptops"}
        description={siteSettings?.meta_description || "Buy certified refurbished phones and laptops at best prices. Get 6 months warranty, COD available, free shipping across India."}
        keywords={siteSettings?.meta_keywords || "refurbished phones, second hand mobile, used laptops, certified refurbished, zobox"}
        ogImage={siteSettings?.og_image || ""}
        ogType="website"
        canonicalUrl="https://zobox.in/shop"
        structuredData={structuredData}
      />

      {/* Category Cards - Buy Phone / Buy Laptop */}
      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Buy Phone Card */}
            <Link
              to="/shop/buy-mobile"
              className="group relative bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col items-center"
            >
              <div className="w-48 h-48 flex items-center justify-center mb-4">
                <img
                  src="https://zobox.in/_next/image?url=%2Fassets%2Fbuy-phone.png&w=750&q=75"
                  alt="Buy Phone"
                  className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Buy Phone</h3>
            </Link>

            {/* Buy Laptop Card */}
            <Link
              to="/shop/buy-laptop"
              className="group relative bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col items-center"
            >
              <div className="w-48 h-48 flex items-center justify-center mb-4">
                <img
                  src="https://zobox.in/_next/image?url=%2Fassets%2Fbuy_laptop.png&w=750&q=75"
                  alt="Buy Laptop"
                  className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Buy Laptop</h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Banners */}
      {banners.length > 0 && (
        <section className="relative">
          <div className="relative">
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={`transition-opacity duration-700 ${
                  index === currentBanner ? 'opacity-100' : 'opacity-0 absolute inset-0'
                }`}
              >
                <Link to={banner.link_url || '/shop/buy-mobile'}>
                  <img
                    src={banner.image_url}
                    alt={banner.title}
                    className="w-full h-auto object-cover"
                  />
                </Link>
              </div>
            ))}
          </div>
          
          {/* Banner Navigation Dots */}
          {banners.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBanner(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentBanner ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Default Banners if no CMS banners */}
      {banners.length === 0 && (
        <section className="bg-gradient-to-r from-[#0ea5e9] to-[#2563eb]">
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <p className="text-lg mb-2">Unlock the</p>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  <span className="bg-cyan-400 px-3 py-1 rounded">BEST DEALS!</span>
                </h2>
                <p className="text-2xl mb-4">—On Latest Smartphones—</p>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-xl">EMI Starting</span>
                  <span className="text-3xl font-bold text-yellow-300">₹2,000/month</span>
                </div>
                <Link
                  to="/shop/buy-mobile"
                  className="inline-block px-8 py-3 bg-white text-[#2B3380] font-semibold rounded-lg hover:bg-gray-100 transition"
                >
                  Explore More
                </Link>
              </div>
              <div className="flex justify-center">
                <img
                  src="https://zobox.in/_next/image?url=%2Fbanners%2Fbanner6.png&w=3840&q=75"
                  alt="Smartphones"
                  className="max-w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Buy Refurbished Phones Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Buy Refurbished Phones</h2>
          {/* Placeholder for product grid - will be implemented later */}
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500">Coming Soon - Browse Refurbished Phones</p>
            <Link to="/shop/buy-mobile" className="inline-block mt-4 text-[#2B3380] font-medium hover:underline">
              View All Phones →
            </Link>
          </div>
        </div>
      </section>

      {/* Buy Refurbished Laptops Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Buy Refurbished Laptops</h2>
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500">Coming Soon - Browse Refurbished Laptops</p>
            <Link to="/shop/buy-laptop" className="inline-block mt-4 text-[#2B3380] font-medium hover:underline">
              View All Laptops →
            </Link>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Brands We Sell</h2>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            {['Apple', 'Samsung', 'OnePlus', 'Xiaomi', 'Vivo', 'Oppo', 'Realme', 'Dell', 'HP', 'Lenovo'].map((brand) => (
              <Link
                key={brand}
                to={`/shop/buy-mobile?brand=${brand.toLowerCase()}`}
                className="px-6 py-3 bg-white rounded-lg shadow-sm hover:shadow-md transition text-gray-700 font-medium"
              >
                {brand}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 bg-[#2B3380]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white">Testimonials</h2>
            <p className="text-white/80 mt-2">Trusted by 10K+ Happy Users and since 2020</p>
          </div>
          
          <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
            {(testimonials.length > 0 ? testimonials : [
              { id: 1, customer_name: 'John Doe', customer_location: 'Delhi', rating: 5, review_text: 'Zobox made it possible for me to own a high-end mobile phone without breaking the bank. Great service and excellent product quality!' },
              { id: 2, customer_name: 'Jane Smith', customer_location: 'Delhi', rating: 4, review_text: 'Zobox made it possible for me to own a high-end mobile phone without breaking the bank. Great service and excellent product quality!' },
              { id: 3, customer_name: 'Sam John', customer_location: 'Delhi', rating: 5, review_text: 'Zobox made it possible for me to own a high-end mobile phone without breaking the bank. Great service and excellent product quality!' },
            ]).map((testimonial) => (
              <div
                key={testimonial.id}
                className="flex-shrink-0 w-72 bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex justify-between items-start mb-4">
                  <img
                    src="https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fin_comma2.dfc616f0.png&w=96&q=75"
                    alt="quote"
                    className="w-8 h-8 opacity-50"
                  />
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {testimonial.review_text}
                </p>
                <div className="flex justify-between items-start">
                  <img
                    src="https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fin_comma1.96ace884.png&w=96&q=75"
                    alt="quote"
                    className="w-8 h-8 opacity-50"
                  />
                </div>
                <div className="flex items-center gap-3 mt-4">
                  {testimonial.customer_image_url ? (
                    <img
                      src={testimonial.customer_image_url}
                      alt={testimonial.customer_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#2B3380] flex items-center justify-center text-white font-semibold">
                      {testimonial.customer_name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.customer_name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.customer_location}</p>
                    <div className="flex gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In the News Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">In the News</h2>
          
          <div className="relative">
            {/* Scroll Buttons */}
            <button
              onClick={() => scrollNews('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            <button
              onClick={() => scrollNews('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>

            <div
              ref={newsScrollRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
            >
              {(news.length > 0 ? news : [
                { id: 1, title: 'Learned skills from abroad, came to the country and tried them, open...', featured_image_url: 'https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fnews1.09a81841.png&w=750&q=75', slug: 'news-1' },
                { id: 2, title: 'Learned skills abroad, sold old phones at half the price in the ...', featured_image_url: 'https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fnews2.43b822fc.png&w=750&q=75', slug: 'news-2' },
                { id: 3, title: "How a Struggling Entrepreneur's Brainwave Made Refurbished Mobil...", featured_image_url: 'https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fnews3.c7de4678.png&w=750&q=75', slug: 'news-3' },
                { id: 4, title: 'Zobox: From vision to revolution, redefining the refurbished gadgets...', featured_image_url: 'https://zobox.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fnews4.bccb1ddb.png&w=750&q=75', slug: 'news-4' },
              ]).map((article) => (
                <div
                  key={article.id}
                  className="flex-shrink-0 w-64 bg-white rounded-xl overflow-hidden shadow-sm border"
                >
                  <div className="aspect-video bg-gray-100">
                    <img
                      src={article.featured_image_url || 'https://via.placeholder.com/300x200'}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-3">
                      {article.title}
                    </h3>
                    <Link
                      to={`/shop/news/${article.slug}`}
                      className="inline-block px-4 py-1.5 bg-[#2B3380] text-white text-sm rounded hover:bg-[#1e2563] transition"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* App Download Banner */}
      <section className="bg-gradient-to-r from-[#2B3380] to-[#1e2563] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Download ZOBIZ App</h2>
              <p className="text-white/80">Get exclusive deals and track your orders on the go</p>
              <div className="flex gap-4 mt-6 justify-center md:justify-start">
                <a
                  href="https://play.google.com/store/search?q=zobiz&c=apps&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://zobox.in/_next/static/media/appButton.79ae55a4.svg"
                    alt="Get it on Google Play"
                    className="h-12"
                  />
                </a>
                <a
                  href="https://www.apple.com/app-store/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://zobox.in/_next/static/media/appStore.c61d19bc.svg"
                    alt="Download on App Store"
                    className="h-12"
                  />
                </a>
              </div>
            </div>
            <div className="hidden md:block">
              <img
                src="https://zobox.in/_next/static/media/HomeBannerrr.c7e28336.svg"
                alt="App Preview"
                className="h-48"
              />
            </div>
          </div>
        </div>
      </section>
    </ShopLayout>
  );
}

export default ShopHomepage;
